import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { differenceInDays } from 'date-fns/differenceInDays';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfWeek } from 'date-fns/startOfWeek';
import { isPresent } from '@ember/utils';
import type { WeekStart } from 'uplisting-frontend/utils/week-start';
import {
  utcDate,
  isSameOrBefore,
  isSameOrAfter,
} from 'uplisting-frontend/utils/date';
import type {
  LocationType,
  CacheForLookup,
  PropertyRowDataItem,
} from 'uplisting-frontend/services/calendar/-internals/types';
import type { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';

const datesCache = {};
const canonicalDatesCache = new Map<number, Date>();

export function getFirstLevelCacheKey(model: LocationType): string {
  return model.id;
}

export function getSecondLevelCacheKey(date: Date): string {
  const time = date.getTime();

  if (datesCache[time]) {
    return datesCache[time];
  }

  const result = utcDate(date).toISOString().slice(0, 10);

  datesCache[time] = result;

  return result;
}

/**
 * Returns an identity-stable, UTC-aligned `Date` for the given input.
 *
 * The calendar grid renders `{{#each viewport.dates}}`, and Glimmer's default
 * `@identity` keying tears down each `DayCell` whenever its iteration item
 * changes object identity. Without this cache, every viewport rebuild
 * (`loadData`, `handleResize`, `addMonthToViewport`, ...) produces fresh
 * `Date` objects for the same logical days, so every cell unmounts and
 * remounts even when nothing meaningful changed.
 *
 * Caching by epoch time guarantees the same logical day always returns the
 * same instance, which lets Glimmer reuse the existing cells and tracked
 * caches across reloads.
 */
export function getCanonicalDate(date: Date): Date {
  const aligned = utcDate(date);
  const time = aligned.getTime();

  const cached = canonicalDatesCache.get(time);

  if (cached) {
    return cached;
  }

  canonicalDatesCache.set(time, aligned);

  return aligned;
}

// The new calendar's cells, and the records in the overlap checks, hold each day
// in its canonical form, which outside UTC sits hours after local midnight.
export function canonicalDay(date: Date): Date {
  return getCanonicalDate(startOfDay(date));
}

/**
 * Check if an occasion overlaps with a specific date
 */
export function occasionOverlapsDate(
  occasionStart: Date,
  occasionEnd: Date,
  date: Date,
): boolean {
  return (
    isSameOrBefore(occasionStart, date) && isSameOrAfter(occasionEnd, date)
  );
}

export function occasionCacheDate(
  occasionStart: Date,
  occasionEnd: Date,
  dates: Date[],
): Date | undefined {
  const [firstDate] = dates;

  if (!firstDate) {
    return undefined;
  }

  const date =
    dates[Math.max(differenceInCalendarDays(occasionStart, firstDate), 0)];

  return date && occasionOverlapsDate(occasionStart, occasionEnd, date)
    ? date
    : undefined;
}

export function forEachOccasionRecord(
  cacheData: CacheForLookup<OccasionRecord[]>,
  callback: (records: OccasionRecord[]) => void,
): void {
  const keys = Object.keys(cacheData);

  for (const id of keys) {
    const cache = cacheData[id];

    if (!cache) {
      continue;
    }

    const records = Object.values(cache).flat();

    if (records.length === 0) {
      continue;
    }

    const sortedOccasions = records.sort(
      (a, b) => a.meta.startDate.getTime() - b.meta.startDate.getTime(),
    );

    callback(sortedOccasions);
  }
}

/**
 * computes row numbers for calendar occasions to prevent visual overlaps in the calendar display.
 *
 * algorithm:
 * 1. process every property occasions separately
 * 2. sort occasions by start date
 * 3. assign each occasion to the first available row where it won't overlap with existing occasions
 * 4. track row availability using end dates to determine when rows become free
 *
 * - occasion 1: Jan 1 - 3 -> row 0
 * - occasion 2: Jan 2 - 4 -> row 1 (overlaps with occasion 1)
 * - occasion 3: Jan 4 - 6 → row 0 (row 0 becomes free after Jan 3)
 */
export function computeOccasionMetaForCalendar(
  records: OccasionRecord[],
): void {
  const rowEndDates: Date[] = [];

  const unique = [...new Set(records)];

  for (const record of unique) {
    let assignedRow = -1;

    for (let i = 0; i < rowEndDates.length; i++) {
      const date = rowEndDates[i];

      if (!date) {
        continue;
      }

      if (date <= record.meta.startDate) {
        assignedRow = i;

        break;
      }
    }

    if (assignedRow === -1) {
      assignedRow = rowEndDates.length;

      rowEndDates.push(record.meta.endDate);
    } else {
      rowEndDates[assignedRow] = record.meta.endDate;
    }

    if (isPresent(record.meta.rowNumber)) {
      continue;
    }

    record.meta.rowNumber = assignedRow;
  }
}

/**
 * the lane a record would occupy if it started on `start`, for the drag preview:
 * it has to stack like the bar it stands in for, at a date the record has not
 * moved to yet, so the packer above can't answer — that one sweeps the row in
 * cache order and writes `meta.rowNumber` as it goes.
 *
 * uses the same overlap test as Data#computeMonthlyOccasionsOverlap, then takes
 * the lowest lane no overlapping neighbour already holds.
 */
export function computeOverlapPlacement(
  record: OccasionRecord,
  start: Date,
  siblings: OccasionRecord[],
): { hasOverlap: boolean; rowNumber: number } {
  const end = addDays(start, record.meta.duration);

  const overlapping = siblings.filter(
    (other) =>
      other !== record &&
      start < other.meta.endDate &&
      end > other.meta.startDate,
  );

  const taken = new Set(overlapping.map((other) => other.meta.rowNumber));

  let rowNumber = 0;

  while (taken.has(rowNumber)) {
    rowNumber += 1;
  }

  return { hasOverlap: overlapping.length > 0, rowNumber };
}

export function belongsToLocation(
  location: LocationType,
  occasion: PropertyRowDataItem,
): boolean {
  const multiUnit = 'multiUnit' in occasion ? occasion.multiUnit : null;

  if (location instanceof MultiUnitModel) {
    return multiUnit?.id === location.id;
  }

  return occasion.property?.id === location.id;
}

export function computeMonthlyEventDuration(
  date: Date | undefined,
  startDate: Date,
  endDate: Date,
  weekStartsOn: WeekStart,
): string {
  if (!date) {
    return '';
  }

  const isOngoing = startDate < date;
  const weekStart = startOfWeek(date, { weekStartsOn });
  const start = isOngoing ? weekStart : startDate;
  const duration = Math.min(differenceInDays(endDate, start), 7);

  return `duration-${duration} ${isOngoing ? 'event-ongoing' : ''}`;
}
